import { useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import { IoIosClose } from 'react-icons/io';
import { TbCircleCheck } from 'react-icons/tb';
import { toast } from 'react-toastify';

import { mensagemDaRecusa, reportRecognition } from 'services/report.service';

export const MIN_REPORT_LENGTH = 20;

interface Props {
  id: string;
  /**
   * 'reported': denúncia enviada com sucesso.
   * 'rejected': servidor recusou por regra de negócio (ex.: já denunciado) —
   *   nesse caso o reconhecimento também já está oculto no servidor.
   * 'close': modal está sendo fechado, sem envio ou após um resultado.
   *
   * 'reported' e 'rejected' significam a mesma coisa para quem escuta: a
   * lista precisa recarregar porque o servidor já não devolve mais este
   * reconhecimento.
   */
  callback: (action: 'reported' | 'rejected' | 'close') => void;
}

export default function ModalReportRecognition({ id, callback }: Props) {
  ReactModal.setAppElement('#modal');

  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message?: string } | undefined>();

  const faltam = useMemo(() => Math.max(0, MIN_REPORT_LENGTH - reason.trim().length), [reason]);
  const formValid = faltam === 0;

  const resultMessage = useMemo(() => {
    if (!result) return '';
    if (result.message) return result.message;
    return result.ok
      ? 'Obrigado por nos avisar. O reconhecimento foi retirado de exibição e será avaliado pela equipe responsável.'
      : 'Não foi possível registrar sua sinalização. Tente novamente em alguns minutos.';
  }, [result]);

  async function handleSubmit() {
    if (!formValid || sending) return;

    setSending(true);

    try {
      await reportRecognition(id, reason.trim());
      setResult({ ok: true });
      callback('reported');
    } catch (error) {
      const recusa = mensagemDaRecusa(error);

      /* Recusa de negócio (BusinessException) tem motivo e vai para a tela —
         e, como as regras que recusam aqui significam "já existe denúncia
         para este id_rec", o servidor já está escondendo o reconhecimento.
         Qualquer outro erro (rede, permissão, exceção interna do trigger)
         não é recusa de negócio: o toast é mais honesto do que promover uma
         mensagem técnica a "motivo da denúncia", e preserva o formulário
         para o jogador tentar de novo. */
      if (recusa) {
        setResult({ ok: false, message: recusa });
        callback('rejected');
      } else {
        toast.error('Ocorreu um erro ao registrar sua sinalização. Por favor, tente novamente em alguns minutos.', {
          position: 'top-right',
          autoClose: 5000,
        });
      }
    } finally {
      setSending(false);
    }
  }

  if (typeof id !== 'string' || !id.length) return <></>;

  return (
    <ReactModal
      shouldCloseOnOverlayClick={false}
      isOpen={true}
      contentLabel="Sinalizar reconhecimento"
      portalClassName="relative z-50"
      overlayClassName="rb-modal-overlay rb-modal-overlay--top"
      contentElement={() => (
        <div className="rb-modal rb-modal--md">
          <button
            className="rb-modal__close"
            aria-label="Fechar"
            onClick={() => !sending && callback('close')}
            disabled={sending}
          >
            <IoIosClose size={26} />
          </button>

          {result ? (
            <>
              <div className="rb-modal__header">
                {result.ok && (
                  <div className="flex items-center justify-center">
                    <TbCircleCheck size={72} color="var(--color-primary)" strokeWidth={1.5} aria-hidden="true" />
                  </div>
                )}
                <h1 className="rb-modal__title">
                  {result.ok ? 'Sinalização enviada' : 'Não foi possível enviar a sinalização'}
                </h1>
              </div>

              <p className="rb-modal__text" style={{ textAlign: 'center' }}>
                {resultMessage}
              </p>

              <div className="rb-modal__actions">
                <button className="rb-btn-primary" onClick={() => callback('close')}>
                  Fechar
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="rb-modal__header">
                <h1 className="rb-modal__title">Sinalizar reconhecimento</h1>
              </div>

              <p className="rb-modal__text">
                Se você considera que este reconhecimento contém conteúdo inadequado, ofensivo ou que não esteja de
                acordo com o propósito do RECONHEÇA, conte para a gente o motivo. O reconhecimento será retirado da
                exibição e o conteúdo será encaminhado para avaliação da equipe responsável.
              </p>

              <div className="rb-modal__field">
                <label className="rb-modal__label" htmlFor="report-reason">
                  Por que você está sinalizando este reconhecimento para análise?
                </label>
                <textarea
                  id="report-reason"
                  className="rb-input"
                  rows={4}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Conte brevemente o que você identificou neste reconhecimento."
                />
                <p className="rb-modal__text" style={{ margin: '6px 0 0', fontSize: 13 }} aria-live="polite">
                  {faltam > 0
                    ? `${faltam > 1 ? 'Faltam' : 'Falta'} ${faltam} caractere${faltam > 1 ? 's' : ''} para o mínimo`
                    : `Mínimo de ${MIN_REPORT_LENGTH} caracteres atingido`}
                </p>
              </div>

              <div className="rb-modal__actions">
                <button className="rb-btn-primary" onClick={handleSubmit} disabled={!formValid || sending}>
                  Enviar sinalização
                </button>
              </div>
            </>
          )}
        </div>
      )}
    />
  );
}
